import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, Loader2, Download, Crown, CheckCircle2 } from "lucide-react";

/**
 * /dashboard/book-cover — AI Book Cover Generator (one free per member) +
 * upsell to the $25 hand-designed cover service (3 revisions included).
 *
 * Backend:
 *   POST /api/book-cover/generate         (one free try, then 429)
 *   GET  /api/book-cover/my               (list past covers)
 *   POST /api/book-cover/order-designed   (request the $25 service)
 */

const NAVY = "#0F172A";
const GOLD  = "#E8B539";
const WINE  = "#0F172A";
const TEAL  = "#287F8E";
const CREAM = "#FFFFFF";

export default function BookCoverPage() {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [triesLeft, setTriesLeft] = useState(5);   // 5 free tries, $79/mo for unlimited
  const isUnlimited = !!user?.is_admin || ["unlimited", "covers_unlimited", "pro_unlimited"].includes((user?.tier || "").toLowerCase());
  const hasFree = isUnlimited || triesLeft > 0;

  // AI form
  const [title, setTitle]       = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [author, setAuthor]     = useState("");
  const [vibe, setVibe]         = useState("warm, faith-aligned, dignified, professional");

  // $25 designed form
  const [showOrder, setShowOrder] = useState(false);
  const [ordering, setOrdering]   = useState(false);
  const [oTitle, setOTitle]       = useState("");
  const [oSubtitle, setOSubtitle] = useState("");
  const [oAuthor, setOAuthor]     = useState("");
  const [oVibe, setOVibe]         = useState("");
  const [oMust, setOMust]         = useState("");

  const refreshMine = async () => {
    try {
      const { data } = await api.get("/book-cover/my");
      setMine(data?.covers || []);
      const used = (data?.covers || []).length;
      setTriesLeft(Math.max(0, 5 - used));
    } catch {}
  };

  useEffect(() => { if (user) refreshMine(); }, [user]);

  if (user === null) return <Navigate to="/login" replace />;
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  const generate = async () => {
    if (!title.trim() || !author.trim()) {
      toast.error("Title and author name are required.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/book-cover/generate", {
        title, subtitle, author_name: author, vibe,
      });
      toast.success("★ Your AI cover is ready!");
      await refreshMine();
    } catch (e) {
      const msg = e.response?.data?.detail || "Cover generation failed.";
      toast.error(msg);
      if (e.response?.status === 429) {
        setTriesLeft(0);
        setShowOrder(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const orderDesigned = async () => {
    if (!oTitle.trim() || !oAuthor.trim()) {
      toast.error("Title and author name are required.");
      return;
    }
    setOrdering(true);
    try {
      await api.post("/book-cover/order-designed", {
        title: oTitle, subtitle: oSubtitle, author: oAuthor,
        vibe: oVibe, must_have: oMust, contact: user.email,
      });
      toast.success("★ Order placed! Mike will be in touch shortly.");
      setShowOrder(false);
      setOTitle(""); setOSubtitle(""); setOAuthor(""); setOVibe(""); setOMust("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't place the order.");
    } finally {
      setOrdering(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="book-cover-page">
      <SiteNav />

      <section className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <Link to="/dashboard"
              data-testid="book-cover-back-btn"
              className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider hover:opacity-80"
              style={{ color: WINE }}>
          <ArrowLeft className="w-3.5 h-3.5" /> My Office
        </Link>

        <div className="mt-4 mb-6">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: GOLD }}>
            ★ Book Cover Studio
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl uppercase leading-tight mt-1"
              style={{ color: NAVY }}>
            Your Book Deserves<br/>A Cover That Stops Folks.
          </h1>
          <p className="text-base md:text-lg italic mt-3" style={{ color: "#3D3D3D" }}>
            Try our AI cover free, once. Want a real designer to do it? $25 — three revisions included.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_380px] gap-6">
          {/* ─── LEFT — AI generator (free once) ─── */}
          <div className="rounded-2xl border-2 bg-white p-5 md:p-6 shadow-md"
               style={{ borderColor: GOLD }}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: TEAL }}>
                ★ AI Cover · 1 Free Try
              </div>
              <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-2.5 py-1 rounded-full"
                    style={{ background: hasFree ? "#BAE6FD" : "#F4E5C4", color: hasFree ? "#2E7D32" : WINE }}>
                {isUnlimited ? "Unlimited" : `${triesLeft} of 5 tries left`}
              </span>
            </div>
            <h2 className="font-serif-display text-2xl uppercase leading-tight" style={{ color: NAVY }}>
              AI-Generated Cover
            </h2>
            <p className="text-sm mt-1" style={{ color: "#3D3D3D" }}>
              Type a few details. We'll spin up a cover in about 30 seconds. {isUnlimited ? "Unlimited covers on your plan." : "5 free tries per account."}
            </p>

            <div className="space-y-3 mt-4">
              <Field label="Book Title" value={title} setValue={setTitle} placeholder="With Faith All Things Are Possible" />
              <Field label="Subtitle (optional)" value={subtitle} setValue={setSubtitle} placeholder="A 30-Day Challenge For The Common Man" />
              <Field label="Author Name" value={author} setValue={setAuthor} placeholder="Mike Boyd" />
              <Field label="Vibe (optional)" value={vibe} setValue={setVibe} placeholder="warm, country, faith, sunrise, hopeful" />
            </div>

            <button
              type="button"
              onClick={generate}
              disabled={loading || !hasFree}
              data-testid="generate-cover-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md text-sm font-bold uppercase tracking-wider shadow disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.01] transition"
              style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? "Generating…" : (hasFree ? (isUnlimited ? "Generate Cover" : `Generate (${triesLeft} left)`) : "All 5 Tries Used")}
            </button>

            {!hasFree && (
              <div className="text-xs mt-3 space-y-1.5" style={{ color: WINE }}>
                <p>
                  All 5 AI tries used. Want a real hand-designed cover with 3 revisions? <button type="button" onClick={() => setShowOrder(true)} className="underline font-bold" data-testid="upgrade-after-used-link">Order the $25 designed cover →</button>
                </p>
                <p>
                  Or upgrade to <strong>Unlimited Covers ($79/mo)</strong> to keep going — <Link to="/pricing" className="underline font-bold" data-testid="upgrade-unlimited-link">see plans →</Link>
                </p>
              </div>
            )}

            {/* Generated covers gallery */}
            {mine.length > 0 && (
              <div className="mt-6">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-2" style={{ color: WINE }}>
                  Your Cover
                </div>
                <div className="grid grid-cols-2 gap-3" data-testid="cover-gallery">
                  {mine.map((c) => (
                    <a key={c.id} href={c.cover_url} target="_blank" rel="noopener noreferrer"
                       data-testid={`cover-thumb-${c.id}`}
                       className="block rounded-lg overflow-hidden border-2 bg-white hover:shadow-md transition"
                       style={{ borderColor: "#E5E1D6" }}>
                      <img src={c.cover_url} alt={c.title} className="w-full h-auto block" loading="lazy" />
                      <div className="p-2 text-xs font-bold" style={{ color: NAVY }}>{c.title}</div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ─── RIGHT — $25 designed upsell ─── */}
          <div className="rounded-2xl border-4 p-5 md:p-6 shadow-md sticky top-6 h-fit"
               style={{ background: "#FFFFFF", borderColor: GOLD }}>
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5" style={{ color: GOLD }} />
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: GOLD }}>
                Premium · Hand-Designed
              </div>
            </div>
            <h2 className="font-serif-display text-3xl uppercase leading-tight" style={{ color: "#FFFFFF" }}>
              We'll Design It For You — <span style={{ color: GOLD }}>$25</span>
            </h2>
            <p className="text-sm mt-2" style={{ color: "#E9DEC5" }}>
              Real human designer. Faitharama-vetted. Three free revisions until you love it.
            </p>

            <ul className="mt-4 space-y-2 text-sm" style={{ color: "#FFFFFF" }}>
              {[
                "Real human designer, not AI",
                "Print-ready paperback + Kindle thumbnail",
                "3 free revisions",
                "Title, subtitle, author block — typeset right",
                "48-hour first draft, most of the time",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => setShowOrder(true)}
              data-testid="open-designed-order-btn"
              className="mt-5 w-full px-5 py-3 rounded-md text-sm font-bold uppercase tracking-wider shadow hover:scale-[1.02] transition"
              style={{ background: GOLD, color: NAVY, border: `2px solid ${"#FFFFFF"}` }}>
              Order A Designed Cover · $25
            </button>
            <div className="text-[10px] mt-2 text-center" style={{ color: "#2E9F3A" }}>
              Mike contacts you within a day to start. Payment confirmed before work begins.
            </div>
          </div>
        </div>
      </section>

      {/* Designed order modal */}
      {showOrder && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
             data-testid="designed-order-modal"
             onClick={() => setShowOrder(false)}>
          <div className="bg-white rounded-2xl border-4 max-w-lg w-full p-6 shadow-2xl"
               onClick={(e) => e.stopPropagation()}
               style={{ borderColor: GOLD }}>
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: WINE }}>
              ★ Order Designed Cover · $25
            </div>
            <h3 className="font-serif-display text-2xl uppercase mt-1 mb-3" style={{ color: NAVY }}>
              Tell Us About Your Book
            </h3>
            <div className="space-y-3">
              <Field label="Book Title *" value={oTitle} setValue={setOTitle} />
              <Field label="Subtitle (optional)" value={oSubtitle} setValue={setOSubtitle} />
              <Field label="Author Name *" value={oAuthor} setValue={setOAuthor} />
              <Field label="Vibe / Style (colors, mood, references)" value={oVibe} setValue={setOVibe} multi />
              <Field label="Must-have elements (cross, sunrise, photo, etc.)" value={oMust} setValue={setOMust} multi />
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button type="button" onClick={() => setShowOrder(false)}
                      className="px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider border-2 hover:bg-white"
                      style={{ borderColor: NAVY, color: NAVY }}>
                Cancel
              </button>
              <button type="button" onClick={orderDesigned} disabled={ordering}
                      data-testid="submit-designed-order-btn"
                      className="px-5 py-2 rounded-md text-xs font-bold uppercase tracking-wider shadow disabled:opacity-50"
                      style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}>
                {ordering ? "Placing…" : "Place Order"}
              </button>
            </div>
            <p className="text-[10px] mt-3" style={{ color: "#7A7066" }}>
              By placing this order you agree we'll contact you within 24 hours to confirm details and collect the $25 payment before work starts. No refunds after the first draft is delivered, but you keep all 3 revisions.
            </p>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}

function Field({ label, value, setValue, placeholder, multi }) {
  const Component = multi ? "textarea" : "input";
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#0F172A" }}>{label}</div>
      <Component
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder || ""}
        rows={multi ? 3 : undefined}
        className="w-full px-3 py-2.5 rounded-md border-2 text-base outline-none focus:border-[#E8B539]"
        style={{ borderColor: "#D6D2C9", background: "#FFFFFF" }}
      />
    </label>
  );
}
